import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@talentmesh/ui/components/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@talentmesh/ui/components/dialog';
import { Field, FieldError, FieldLabel } from '@talentmesh/ui/components/field';
import { Input } from '@talentmesh/ui/components/input';
import { Textarea } from '@talentmesh/ui/components/textarea';
import { TypographyP } from '@talentmesh/ui/components/typography';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { criteriaScoreFieldNames, CriteriaScoreFormValues, criteriaScoreSchema } from '../CriteriaScore';
import { ExperienceCriterionFinalScore } from '@/Models/ExperienceCriterionFinalScore';
import UIStrings from '@/Utils/UIStrings';

interface CriteriaScoreFormDialogProps {
    open: boolean;
    criteriaFinalScore: ExperienceCriterionFinalScore;
    onClose: () => void;
    onSubmit: (form: CriteriaScoreFormValues, criterionScore: ExperienceCriterionFinalScore) => Promise<void>;
}

const CriteriaScoreFormDialog = ({ criteriaFinalScore, open, onClose, onSubmit }: CriteriaScoreFormDialogProps) => {
    const form = useForm({ resolver: zodResolver(criteriaScoreSchema), mode: 'all' });
    const isFormEqualOnSubmit = !form.formState.isDirty && form.formState.isSubmitted;

    useEffect(() => {
        if (open && criteriaFinalScore) {
            form.reset({
                userDefinedScore: criteriaFinalScore.criterionScore?.score,
                userDefinedJustification: criteriaFinalScore.justification ?? undefined,
            });
        }
    }, [open, criteriaFinalScore, form]);

    const handleSubmit = async (formValues: CriteriaScoreFormValues) => {
        if (form.formState.isDirty) {
            await onSubmit(formValues, criteriaFinalScore);
        }
    };

    return (
        <FormProvider {...form}>
            <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
                <DialogContent className="max-w-xl" showCloseButton>
                    <DialogHeader>
                        <DialogTitle>{UIStrings.NewJustificationAndScore}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-6">
                        <div className="my-6 flex flex-col gap-8 py-2">
                            <Controller
                                name={criteriaScoreFieldNames.userDefinedScore}
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <Field data-invalid={fieldState.error ? true : undefined} className="w-full">
                                        <FieldLabel htmlFor="user-defined-score">{UIStrings.NewScore}</FieldLabel>
                                        <NumericFormat
                                            customInput={Input}
                                            getInputRef={field.ref}
                                            thousandSeparator
                                            allowNegative={false}
                                            id="user-defined-score"
                                            name={field.name}
                                            value={field.value === undefined || field.value === null ? '' : field.value}
                                            onValueChange={(values) => {
                                                field.onChange(
                                                    values.floatValue === undefined ? undefined : values.floatValue,
                                                );
                                            }}
                                            onBlur={field.onBlur}
                                            aria-invalid={fieldState.error ? true : undefined}
                                        />
                                        <FieldError>{fieldState.error?.message}</FieldError>
                                    </Field>
                                )}
                            />
                            <Controller
                                name={criteriaScoreFieldNames.userDefinedJustification}
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <Field data-invalid={fieldState.error ? true : undefined} className="w-full">
                                        <FieldLabel htmlFor="user-defined-justification">
                                            {UIStrings.NewJustification}
                                        </FieldLabel>
                                        <Textarea
                                            id="user-defined-justification"
                                            rows={6}
                                            className="field-sizing-fixed resize-none"
                                            {...field}
                                            value={field.value ?? ''}
                                            aria-invalid={fieldState.error ? true : undefined}
                                        />
                                        <FieldError>{fieldState.error?.message}</FieldError>
                                    </Field>
                                )}
                            />
                            {isFormEqualOnSubmit && (
                                <TypographyP className="text-destructive">
                                    {UIStrings.PleaseUpdateScoreAndJustificationToProceed}
                                </TypographyP>
                            )}
                        </div>
                        <DialogFooter>
                            <Button type="submit" size="lg" disabled={form.formState.isSubmitting}>
                                {form.formState.isSubmitting ? (
                                    <Loader2 className="mr-2 size-4 shrink-0 animate-spin" aria-hidden />
                                ) : null}
                                {UIStrings.Update}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </FormProvider>
    );
};

export default CriteriaScoreFormDialog;
